import {
    MarginCssStyleDeclarations,
    MarginCssStyleDeclarationsAsString
} from '../components/action-item/entities/margin-style.type';

// How the following helper functions work in fixed positioning scenario
// The styles properties are always the distance to its border
// -> style.right = distrance from the element to the right of the screen
// -> style.bottom = distance from the element to the bottom of the screen

// DOMRect information is a little different
// DOMRect.right is the distance from the left side of the screen to the right side of the targetted element

// example: child.style.right = `calc(100vw - ${parentRect?.left ?? 0}px)`;
// The right side of the child is aligned to the left side using this trick
// Applying 100vw to styles.right makes the element moves the element all the way to the left, making the element dissapear from the page
// by applying the negative left, we move the element to the right, aligning it perfectly with the targetted element

export function fixElementTopToParentTop(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min = 0;
    const max: number = window.innerHeight - child.getBoundingClientRect().height;

    const repositionValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.top,
        'marginTop',
        'margin-top',
        child
    );
    child.style.top = repositionValue + 'px';
}

export function fixElementTopToParentBottom(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min = 0;
    const max: number = window.innerHeight - child.getBoundingClientRect().height;

    const repositionedValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.bottom,
        'marginTop',
        'margin-top',
        child
    );
    child.style.top = `${repositionedValue}px`;
}

export function fixElementBottomToParentBottom(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min: number = child.getBoundingClientRect().height;
    const max: number = window.innerHeight;

    const repositionValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.bottom,
        'marginBottom',
        'margin-bottom',
        child
    );
    child.style.bottom = `calc(100vh - ${repositionValue}px)`;
}

export function fixElementBottomToParentTop(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min: number = child.getBoundingClientRect().height;
    const max: number = window.innerHeight;

    const repositionedValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.top,
        'marginBottom',
        'margin-bottom',
        child
    );
    child.style.bottom = `calc(100vh - ${repositionedValue}px)`;
}

export function fixElementLeftToParentLeft(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min = 0;
    const max: number = document.body.scrollWidth - child.getBoundingClientRect().width;

    const repositionValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.left ?? 0,
        'marginLeft',
        'margin-left',
        child
    );
    child.style.left = repositionValue + 'px';
}

export function fixElementLeftToParentRight(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min = 0;
    const max: number = document.body.scrollWidth - child.getBoundingClientRect().width;

    const repositionValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.right ?? 0,
        'marginLeft',
        'margin-left',
        child
    );
    child.style.left = repositionValue + 'px';
}

export function fixElementRightToParentLeft(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min: number = child.getBoundingClientRect().width;
    const max: number = document.body.scrollWidth;
    const scrollBarWidth: number = window.innerWidth - document.body.scrollWidth;

    const repositionValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.left,
        'marginRight',
        'margin-right',
        child
    );
    child.style.right = `calc(100vw - ${scrollBarWidth}px - ${repositionValue}px)`;
}

export function fixElementRightToParentRight(child: HTMLElement, parentRect: DOMRect | undefined): void {
    const min: number = child.getBoundingClientRect().width;
    const max: number = document.body.scrollWidth;
    const scrollBarWidth: number = window.innerWidth - document.body.scrollWidth;

    const repositionedValue: number = repositionFixedDomElement(
        min,
        max,
        parentRect?.right,
        'marginRight',
        'margin-right',
        child
    );
    child.style.right = `calc(100vw - ${scrollBarWidth}px - ${repositionedValue}px)`;
}

function repositionFixedDomElement(
    min: number,
    max: number,
    wish: number = 0,
    cssDeclarationToClear: MarginCssStyleDeclarations,
    cssDeclarationAsString: MarginCssStyleDeclarationsAsString,
    element: HTMLElement
): number {
    let sanitizeWishValue: number = wish;

    if (sanitizeWishValue < min) {
        sanitizeWishValue = min;
    } else if (sanitizeWishValue > max) {
        sanitizeWishValue = max;
    }

    if (isValueAtBoundaries(sanitizeWishValue, min, max)) {
        element.style[cssDeclarationToClear] = '0';
    } else {
        element.style.removeProperty(cssDeclarationAsString);
    }

    return sanitizeWishValue;
}

function isValueAtBoundaries(actualValue: number, minValue: number, maxValue: number): boolean {
    return actualValue === minValue || actualValue === maxValue;
}
